// Página para que presidente_club (o meet_director) postule un evento (competencia)
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { postularEvento } from '../services/eventosService';
import { getPerfiles } from '../services/authService';
import { REGIONES, getComunasPorRegion } from '../data/regionesYComunas';

const styles = {
  container: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '2rem 1rem'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1a4d8c',
    marginBottom: '0.5rem'
  },
  subtitle: {
    color: '#666',
    marginBottom: '1.5rem'
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '1.5rem',
    border: '1px solid #eaeaea'
  },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '1rem' },
  label: { fontSize: '13px', color: '#555', fontWeight: 600 },
  input: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px'
  },
  textarea: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '100px',
    resize: 'vertical'
  },
  row: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '1.5rem' },
  btnPrimary: {
    padding: '10px 20px',
    background: '#1a4d8c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },
  btnGhost: {
    padding: '10px 20px',
    background: '#f4f6f8',
    color: '#111',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'none'
  },
  error: { color: '#c62828', fontSize: '14px', marginTop: '0.5rem' },
  success: { color: '#2e7d32', fontSize: '14px', marginTop: '0.5rem' }
};

const TIPOS_EVENTO = [
  { value: 'competencia', label: 'Competencia' },
  { value: 'meet', label: 'Meet' },
  { value: 'torneo', label: 'Torneo' },
  { value: 'exhibicion', label: 'Exhibición' }
];

const PostularEventoPage = () => {
  // Solo presidentes de club y meet directors pueden postular (el backend también lo valida).
  // undefined = verificando, true/false = resultado
  const [puedePostular, setPuedePostular] = useState(undefined);
  useEffect(() => {
    let cancelado = false;
    getPerfiles()
      .then((perfiles) => {
        if (!cancelado) setPuedePostular(perfiles.includes('presidente_club') || perfiles.includes('meet_director'));
      })
      .catch(() => { if (!cancelado) setPuedePostular(true); }); // si falla la consulta, decide el backend al enviar
    return () => { cancelado = true; };
  }, []);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    tipo_evento: 'competencia',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
    fecha_limite_inscripcion: '',
    ciudad: '',
    region: '',
    ubicacion: '',
    cupos_maximos: '',
    // costo_inscripcion: deprecado (se mantiene por compatibilidad backend)
    costo_inscripcion: '0',
    incluye_tested: false,
    incluye_untested: false,
    categorias_incluidas: [],
    costos_inscripcion_detalle: {
      untested_con_club: '',
      untested_independiente: '',
      tested_con_club: '',
      tested_independiente: '',
    },
    formulario_inscripcion_tipo: 'externo',
    formulario_inscripcion_url: '',
    modalidad_extra_habilitada: false,
    modalidad_extra_monto: '',
    edad_extra_habilitada: false,
    edad_extra_monto: '', // se mantiene por compat (mismo valor que modalidad extra)
    divisiones: ''
  });

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nombre || !form.fecha_inicio || !form.fecha_fin || !form.fecha_limite_inscripcion) {
      setError('Completa nombre y todas las fechas.');
      return;
    }
    const inicio = new Date(form.fecha_inicio);
    const limite = new Date(form.fecha_limite_inscripcion);
    if (limite.getTime() >= inicio.getTime()) {
      setError('La fecha límite de inscripción debe ser anterior a la fecha de inicio del evento.');
      return;
    }
    setSaving(true);
    try {
      await postularEvento({
        nombre: form.nombre,
        tipo_evento: form.tipo_evento,
        descripcion: form.descripcion || undefined,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        fecha_limite_inscripcion: form.fecha_limite_inscripcion,
        ciudad: form.ciudad || undefined,
        region: form.region || undefined,
        ubicacion: form.ubicacion || undefined,
        cupos_maximos: form.cupos_maximos ? Number(form.cupos_maximos) : undefined,
        costo_inscripcion: form.costo_inscripcion ? Number(form.costo_inscripcion) : 0,
        incluye_tested: !!form.incluye_tested,
        incluye_untested: !!form.incluye_untested,
        categorias_incluidas: Array.isArray(form.categorias_incluidas) ? form.categorias_incluidas : [],
        costos_inscripcion_detalle: {
          untested_con_club: form.costos_inscripcion_detalle?.untested_con_club === '' ? null : Number(form.costos_inscripcion_detalle?.untested_con_club),
          untested_independiente: form.costos_inscripcion_detalle?.untested_independiente === '' ? null : Number(form.costos_inscripcion_detalle?.untested_independiente),
          tested_con_club: form.costos_inscripcion_detalle?.tested_con_club === '' ? null : Number(form.costos_inscripcion_detalle?.tested_con_club),
          tested_independiente: form.costos_inscripcion_detalle?.tested_independiente === '' ? null : Number(form.costos_inscripcion_detalle?.tested_independiente),
        },
        formulario_inscripcion_tipo: form.formulario_inscripcion_tipo === 'interno' ? 'interno' : 'externo',
        formulario_inscripcion_url: form.formulario_inscripcion_tipo === 'externo' ? (form.formulario_inscripcion_url || null) : null,
        modalidad_extra_habilitada: !!form.modalidad_extra_habilitada,
        modalidad_extra_monto: form.modalidad_extra_habilitada
          ? (form.modalidad_extra_monto === '' ? 0 : Number(form.modalidad_extra_monto))
          : 0,
        edad_extra_habilitada: !!form.edad_extra_habilitada,
        // edad_extra_monto NO se envía: usa el mismo valor que modalidad_extra_monto
        divisiones: form.divisiones
          ? form.divisiones.split(',').map(x => x.trim()).filter(Boolean)
          : []
      });
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err) {
      setError(err.message || 'Error al postular el evento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header />
      <div style={styles.container}>
        <h1 style={styles.title}>Postular un Evento (Competencia)</h1>
        <p style={styles.subtitle}>
          Como presidente de club puedes proponer un evento para que sea revisado y publicado por el equipo administrativo.
        </p>

        <div style={styles.card}>
          {puedePostular === undefined ? (
            <p style={{ margin: 0, color: '#666' }}>Cargando…</p>
          ) : puedePostular === false ? (
            <div>
              <p style={{ marginTop: 0, fontWeight: 600, color: '#111' }}>
                Solo presidentes de club y meet directors pueden postular eventos.
              </p>
              <p style={{ color: '#555' }}>
                Si quieres organizar una competencia, habla con el presidente de tu club o escríbenos para habilitar tu perfil.
              </p>
              <Link to="/events" style={{ color: '#D52B1E', fontWeight: 600 }}>Ver próximos eventos →</Link>
            </div>
          ) : success ? (
            <p style={styles.success}>
              Evento postulado correctamente. Será revisado por administración. Redirigiendo a Mi perfil…
            </p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label}>Nombre del evento *</label>
                <input
                  style={styles.input}
                  value={form.nombre}
                  onChange={e => setField('nombre', e.target.value)}
                  placeholder="Ej: Campeonato Regional 2025"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Tipo de evento</label>
                <select
                  style={styles.input}
                  value={form.tipo_evento}
                  onChange={e => setField('tipo_evento', e.target.value)}
                >
                  {TIPOS_EVENTO.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Descripción</label>
                <textarea
                  style={styles.textarea}
                  value={form.descripcion}
                  onChange={e => setField('descripcion', e.target.value)}
                  placeholder="Descripción del evento, bases, categorías..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div style={styles.field}>
                  <label style={styles.label}>Fecha inicio *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={form.fecha_inicio}
                    onChange={e => setField('fecha_inicio', e.target.value)}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Fecha fin *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={form.fecha_fin}
                    onChange={e => setField('fecha_fin', e.target.value)}
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Límite inscripción * <span style={{ fontWeight: 'normal', color: '#666', fontSize: '0.85rem' }}>(antes del inicio del evento)</span></label>
                  <input
                    type="date"
                    style={styles.input}
                    value={form.fecha_limite_inscripcion}
                    onChange={e => setField('fecha_limite_inscripcion', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <div style={styles.field}>
                  <label style={styles.label}>Modalidad de competencia</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!form.incluye_tested}
                        onChange={e => setField('incluye_tested', e.target.checked)}
                      />
                      TESTED
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!form.incluye_untested}
                        onChange={e => setField('incluye_untested', e.target.checked)}
                      />
                      UNTESTED
                    </label>
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Categorías incluidas</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {[
                      { value: 'sub_junior', label: 'Sub junior' },
                      { value: 'junior', label: 'Junior' },
                      { value: 'open', label: 'Open' },
                      { value: 'master', label: 'Master' },
                    ].map((c) => (
                      <label key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={(form.categorias_incluidas || []).includes(c.value)}
                          onChange={() => {
                            const arr = Array.isArray(form.categorias_incluidas) ? [...form.categorias_incluidas] : [];
                            const idx = arr.indexOf(c.value);
                            const next = idx >= 0 ? arr.filter(x => x !== c.value) : [...arr, c.value];
                            setField('categorias_incluidas', next);
                          }}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Costos de inscripción</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={styles.field}>
                      <label style={styles.label}>Untested con club</label>
                      <input
                        type="number"
                        style={{ ...styles.input, opacity: form.incluye_untested ? 1 : 0.6 }}
                        disabled={!form.incluye_untested}
                        value={form.costos_inscripcion_detalle?.untested_con_club ?? ''}
                        onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), untested_con_club: e.target.value })}
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Untested independiente</label>
                      <input
                        type="number"
                        style={{ ...styles.input, opacity: form.incluye_untested ? 1 : 0.6 }}
                        disabled={!form.incluye_untested}
                        value={form.costos_inscripcion_detalle?.untested_independiente ?? ''}
                        onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), untested_independiente: e.target.value })}
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Tested con club</label>
                      <input
                        type="number"
                        style={{ ...styles.input, opacity: form.incluye_tested ? 1 : 0.6 }}
                        disabled={!form.incluye_tested}
                        value={form.costos_inscripcion_detalle?.tested_con_club ?? ''}
                        onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), tested_con_club: e.target.value })}
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Tested independiente</label>
                      <input
                        type="number"
                        style={{ ...styles.input, opacity: form.incluye_tested ? 1 : 0.6 }}
                        disabled={!form.incluye_tested}
                        value={form.costos_inscripcion_detalle?.tested_independiente ?? ''}
                        onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), tested_independiente: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Formulario de inscripción</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="radio"
                        name="formulario_inscripcion_tipo"
                        checked={form.formulario_inscripcion_tipo !== 'interno'}
                        onChange={() => setField('formulario_inscripcion_tipo', 'externo')}
                      />
                      Formulario externo
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="radio"
                        name="formulario_inscripcion_tipo"
                        checked={form.formulario_inscripcion_tipo === 'interno'}
                        onChange={() => setField('formulario_inscripcion_tipo', 'interno')}
                      />
                      Formulario interno
                    </label>
                  </div>
                  {form.formulario_inscripcion_tipo !== 'interno' && (
                    <input
                      style={{ ...styles.input, marginTop: 10 }}
                      value={form.formulario_inscripcion_url || ''}
                      onChange={e => setField('formulario_inscripcion_url', e.target.value)}
                      placeholder="URL del formulario externo (https://...)"
                    />
                  )}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Modalidad extra</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!form.modalidad_extra_habilitada}
                        onChange={e => setField('modalidad_extra_habilitada', e.target.checked)}
                      />
                      Existe modalidad extra
                    </label>
                    <input
                      type="number"
                      style={{ ...styles.input, width: 200, opacity: form.modalidad_extra_habilitada ? 1 : 0.6 }}
                      disabled={!form.modalidad_extra_habilitada}
                      value={form.modalidad_extra_monto ?? ''}
                      onChange={e => setField('modalidad_extra_monto', e.target.value)}
                      placeholder="Monto modalidad extra (ej: 15000)"
                    />
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>División de edad extra</label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!form.edad_extra_habilitada}
                        onChange={e => setField('edad_extra_habilitada', e.target.checked)}
                      />
                      Existe división de edad extra
                    </label>
                    <span style={{ fontSize: 13, color: '#666' }}>
                      Costo: usa el mismo valor que “Modalidad extra”.
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.field}>
                  <label style={styles.label}>Región</label>
                  <select
                    style={styles.input}
                    value={form.region}
                    onChange={e => {
                      const region = e.target.value;
                      setField('region', region);
                      const comunas = getComunasPorRegion(region);
                      if (form.ciudad && !comunas.includes(form.ciudad)) {
                        setField('ciudad', '');
                      }
                    }}
                  >
                    <option value="">Seleccione región</option>
                    {REGIONES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Ciudad / Comuna</label>
                  <select
                    style={styles.input}
                    value={form.ciudad}
                    onChange={e => setField('ciudad', e.target.value)}
                    disabled={!form.region}
                  >
                    <option value="">Seleccione ciudad</option>
                    {form.ciudad && form.region && !getComunasPorRegion(form.region).includes(form.ciudad) && (
                      <option value={form.ciudad}>{form.ciudad}</option>
                    )}
                    {getComunasPorRegion(form.region).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Ubicación / Dirección</label>
                <input
                  style={styles.input}
                  value={form.ubicacion}
                  onChange={e => setField('ubicacion', e.target.value)}
                  placeholder="Lugar o dirección del evento"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.field}>
                  <label style={styles.label}>Cupos máximos</label>
                  <input
                    type="number"
                    min="1"
                    style={styles.input}
                    value={form.cupos_maximos}
                    onChange={e => setField('cupos_maximos', e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Costo inscripción (CLP)</label>
                  <input
                    type="number"
                    min="0"
                    style={styles.input}
                    value={form.costo_inscripcion}
                    onChange={e => setField('costo_inscripcion', e.target.value)}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Divisiones (separadas por coma)</label>
                <input
                  style={styles.input}
                  value={form.divisiones}
                  onChange={e => setField('divisiones', e.target.value)}
                  placeholder="Ej: Masculino, Femenino"
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.row}>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                  {saving ? 'Enviando…' : 'Postular evento'}
                </button>
                <Link to="/profile" style={styles.btnGhost}>
                  Volver a Mi perfil
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PostularEventoPage;
